import type { Asset, LogoStrategies } from '@seek/brand-types';
import { BrandLogo } from '@seek/branding-assets';
import { useTranslations } from '@vocab/react';
import {
  Heading,
  IconChevron,
  Text,
  Stack,
  Box,
  Inline,
  Columns,
  Column,
  List,
} from 'braid-design-system';
import React from 'react';

import translations from './.vocab';

import * as styles from './index.css';

export interface Props {
  advertiserName: string;
  bullets: string[];
  category: string;
  displayPay: boolean;
  isFeatured: boolean;
  jobTitle: string;
  location: string;
  logo?: Asset<LogoStrategies>;
  salaryText: string;
  salaryCurrency?: string | null;
  searchDescription: string;
  showBullets: boolean;
  subcategory: string;
  isStyleEnabled: boolean;
}

// This is the name DACs call the component that's used in SERP and other places
// It will come from metropolis at some point once DACs migrate it
export const JobCard = ({
  advertiserName,
  bullets,
  category,
  displayPay,
  jobTitle,
  isFeatured,
  isStyleEnabled,
  location,
  logo,
  salaryText,
  salaryCurrency,
  searchDescription,
  showBullets,
  subcategory,
}: Props) => {
  const { t } = useTranslations(translations);

  const jobClassName = [
    styles.root,
    {
      [styles.isFeatured]: isFeatured,
      [styles.isStyled]: !isFeatured && isStyleEnabled,
    },
  ];

  return (
    <Box className={jobClassName}>
      <Stack space="medium">
        <Stack space="small">
          <Columns space="none">
            <Column>
              <Heading level="3">
                <Box component="span" className={styles.jobTitle}>
                  {jobTitle}
                </Box>
              </Heading>
            </Column>
            {isFeatured ? (
              <Column width="content">
                <Text>
                  <Box component="span" className={styles.jobTitle}>
                    {t('job-card-featured-text')}
                  </Box>
                </Text>
              </Column>
            ) : null}
          </Columns>
          <Text weight="medium" size="large">
            {advertiserName}
          </Text>
        </Stack>

        <Stack space="small">
          <Text size="small">{location}</Text>

          {Boolean(salaryText) && displayPay && (
            <Text size="small">
              {salaryText}
              {salaryCurrency ? ` (${salaryCurrency})` : ''}
            </Text>
          )}
          <Inline align="left" space="xxsmall" alignY="center">
            <Text size="small">{category}</Text>
            <Text size="small">
              <IconChevron direction="right" tone="secondary" />
            </Text>
            <Text size="small">{subcategory}</Text>
          </Inline>
        </Stack>

        {showBullets && (
          <List type="bullet" space="small" size="small">
            {bullets
              .filter((item) => item.length > 0)
              .map((ksp: string, index) => (
                <Text key={`ksp-${index + 1}`}>{ksp}</Text>
              ))}
          </List>
        )}

        <Box paddingRight="xxlarge">
          <Text size="small" tone="secondary">
            {searchDescription}
          </Text>
        </Box>
      </Stack>
      {logo && (
        <Box display="flex" justifyContent="flexEnd">
          <BrandLogo placement="serp" logo={logo} />
        </Box>
      )}
    </Box>
  );
};
